import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { Graduation } from '../../types/aai';
import {
  Action, Chip, Divided, Drawer, EmptyState, ErrorNote, Kicker, ListRow, Panel, PanelHead, RouteMark,
} from '../../components/aa';
import { relativeTime } from '../partner/format';

/*
 * Shares waiting on the program team.
 *
 * A person builds a skill on their own work, uses it, and records how each run
 * went. Once it has worked enough times they may offer it to their team — and
 * that offer is where the loop used to stop: the decision existed only on the
 * old marketplace screen, so a proposal sat unanswered while the count of them
 * was displayed elsewhere.
 *
 * Approving publishes the skill to the audience the proposal names. The
 * evidence shown is the skill's own run record and nothing else: how often it
 * worked is a fact about the skill, not a rating of the person who wrote it.
 */

const audienceOf = (g: Graduation): string => {
  if (g.to_scope === 'company') return 'Everyone in the company';
  if (g.department) return `The ${g.department} team`;
  return 'Their team';
};

const evidenceLine = (g: Graduation): string => {
  const e = g.effectiveness;
  if (!e) return 'No run record was attached.';
  const parts = [`${e.worked} of ${e.runs} runs worked`];
  if (e.success_rate !== null && e.success_rate !== undefined) parts.push(`${Math.round(e.success_rate * 100)}% success`);
  if (e.versions > 0) parts.push(`${e.versions} ${e.versions === 1 ? 'revision' : 'revisions'}`);
  if (e.adoption > 0) parts.push(`${e.adoption} ${e.adoption === 1 ? 'person' : 'people'} already used it`);
  return parts.join(' · ');
};

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-card border border-line bg-canvas-inset px-5 py-4">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1.5 break-words text-[15px] font-bold text-ink">{value}</div>
  </div>
);

const NOTE = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';

/** One proposal, opened to decide. Declining always carries a reason. */
const ShareDrawer: React.FC<{
  proposal: Graduation;
  onDecided: () => void;
  onClose: () => void;
}> = ({ proposal, onDecided, onClose }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'approved' | 'declined' | null>(null);

  const decide = async (approve: boolean) => {
    if (!approve && !note.trim()) {
      toast.error('Say why, in a sentence. The person who offered it reads this.');
      return;
    }
    setBusy(true);
    try {
      await aaiApi.decideGraduation(proposal.id, approve, note.trim() || undefined);
      setDone(approve ? 'approved' : 'declined');
      onDecided();
      if (approve) toast.success(`Shared. ${audienceOf(proposal)} can use it now.`);
      if (!approve) toast.success('Declined, with your reason on the record.');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'That decision did not record.');
    } finally {
      setBusy(false);
    }
  };

  let badge = <Chip tone="warn" dot>Waiting on you</Chip>;
  if (done === 'approved') badge = <Chip tone="ok">Shared</Chip>;
  if (done === 'declined') badge = <Chip tone="err">Declined</Chip>;

  let sub = `${proposal.proposed_by} offers this to ${audienceOf(proposal).toLowerCase()}. Approving publishes it to them; declining keeps it personal.`;
  if (done === 'approved') sub = `Published to ${audienceOf(proposal).toLowerCase()}. It is now in the company skills library.`;
  if (done === 'declined') sub = 'Declined. The skill stays personal to its author, who can revise it and offer it again.';

  let footer = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <Action size="lg" variant="quiet" onClick={() => decide(false)} disabled={busy} reason="Recording">Decline with a reason</Action>
      <Action size="lg" onClick={() => decide(true)} disabled={busy} reason="Recording">Approve the share</Action>
    </div>
  );
  if (done) footer = <div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>;

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Skill sharing</Kicker>}
      badge={badge}
      title={proposal.skill_name}
      sub={sub}
      footer={footer}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Tile label="Offered by" value={`${proposal.proposed_by} · ${relativeTime(proposal.created_at)}`} />
        <Tile label="Audience" value={audienceOf(proposal)} />
        <Tile label="Run record" value={evidenceLine(proposal)} />
        <Tile label="Today" value={proposal.from_scope === 'personal' ? 'Personal to its author' : proposal.from_scope} />
      </div>

      <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">
        The run record is the skill&apos;s own: how many times it was used and how often it worked. It says nothing
        about anything else its author does, and it became visible only because they offered the skill.
      </p>

      {!done && (
        <div className="mt-6">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Note for the author</div>
          <textarea
            className={NOTE}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Required when declining. Optional when approving."
          />
        </div>
      )}
    </Drawer>
  );
};

/** The queue panel. Renders nothing when the queue is empty and `hideEmpty` is set. */
export const SharePanel: React.FC<{ className?: string; hideEmpty?: boolean }> = ({ className = '', hideEmpty = false }) => {
  const queue = useAsync<{ graduations: Graduation[] }>(() => aaiApi.getGraduationQueue(), 'Could not load skills waiting to be shared.');
  const [open, setOpen] = useState<Graduation | null>(null);

  const items = queue.data?.graduations || [];
  if (hideEmpty && items.length === 0 && !queue.error) return null;

  return (
    <>
      <Panel className={className}>
        <PanelHead
          kicker="Skill sharing"
          title={items.length === 0 ? 'No skill is waiting to be shared' : `${items.length === 1 ? '1 skill' : `${items.length} skills`} offered to a team`}
          desc="Somebody built a skill on their own work, used it, and offered it to their colleagues. Approving publishes it to the audience they named."
        />
        {queue.error && <div className="px-6 py-5"><ErrorNote message={queue.error} /></div>}
        {!queue.error && items.length === 0 && (
          <EmptyState
            title="Nothing offered yet"
            reason="A proposal appears here once a person has used a skill of their own enough times for it to count as working, and offers it to their team."
          />
        )}
        {items.length > 0 && (
          <Divided>
            {items.map((g) => (
              <ListRow
                key={g.id}
                mark={<RouteMark kind="quick_win" size="lg" />}
                title={g.skill_name}
                badge={<Chip tone="warn" dot>Waiting on you</Chip>}
                desc={evidenceLine(g)}
                meta={[
                  { label: 'Offered by', value: g.proposed_by, foot: relativeTime(g.created_at) },
                  { label: 'Audience', value: audienceOf(g) },
                ]}
                right={<Action size="sm" arrow onClick={() => setOpen(g)}>Review the share</Action>}
              />
            ))}
          </Divided>
        )}
      </Panel>

      {open && (
        <ShareDrawer
          proposal={open}
          onDecided={() => { queue.reload(); }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
};

export default SharePanel;
